import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "wouter";
import {
  inferPortalModuleFromPath,
  isPortalModule,
  type PortalModule,
} from "@shared/portalModules";

/** Backward-compatible two-way mode for existing Sales/Service callers. */
export type PortalMode = "service" | "sales";

const STORAGE_KEY = "clarity:portalModule";
const LEGACY_STORAGE_KEY = "clarity:portalMode";
/** Set once per tab after the default-module landing has been applied. */
export const ROOT_REDIRECT_FLAG = "clarity:rootRedirectApplied";

function readModuleFromStorage(): PortalModule {
  if (typeof window === "undefined") return "service";
  const current = sessionStorage.getItem(STORAGE_KEY);
  if (isPortalModule(current)) return current;
  // Migrate the old sales/service key if present.
  const legacy = sessionStorage.getItem(LEGACY_STORAGE_KEY);
  if (isPortalModule(legacy)) {
    sessionStorage.setItem(STORAGE_KEY, legacy);
    return legacy;
  }
  return "service";
}

function persistModule(module: PortalModule) {
  if (typeof sessionStorage !== "undefined") {
    sessionStorage.setItem(STORAGE_KEY, module);
  }
}

type Ctx = {
  module: PortalModule;
  /** Sales/Service projection for legacy callers (procurement → service styling). */
  mode: PortalMode;
  isSales: boolean;
  isProcurement: boolean;
  setModule: (m: PortalModule) => void;
  /** @deprecated use setModule — kept for backward compatibility. */
  setMode: (m: PortalMode) => void;
  enterSales: () => void;
  enterService: () => void;
  enterProcurement: () => void;
};

const PortalModeContext = createContext<Ctx | null>(null);

export function PortalModeProvider({ children }: { children: ReactNode }) {
  const [location, setLocation] = useLocation();
  const [module, setModuleState] = useState<PortalModule>(() => readModuleFromStorage());

  const applyModule = useCallback((m: PortalModule) => {
    setModuleState((prev) => {
      if (prev !== m) persistModule(m);
      return m;
    });
  }, []);

  const setModule = useCallback(
    (m: PortalModule) => {
      applyModule(m);
    },
    [applyModule],
  );

  const setMode = useCallback((m: PortalMode) => applyModule(m), [applyModule]);

  const enterSales = useCallback(() => {
    applyModule("sales");
    setLocation("/sales");
  }, [applyModule, setLocation]);

  const enterService = useCallback(() => {
    applyModule("service");
    setLocation("/");
  }, [applyModule, setLocation]);

  const enterProcurement = useCallback(() => {
    applyModule("procurement");
    setLocation("/procurement");
  }, [applyModule, setLocation]);

  // Sync module from the URL. Deep links determine the active module; shared
  // routes (e.g. /customers) preserve whatever module the user was already in.
  useEffect(() => {
    const browserPath = typeof window !== "undefined" ? window.location.pathname : location;
    const inferred = inferPortalModuleFromPath(location);
    if (inferred == null) {
      // Shared route — keep current module, but honour nested D-Tools upload deep links.
      if (browserPath.includes("/dtools-json-upload/")) applyModule("sales");
      return;
    }
    // Nested D-Tools upload deep links can resolve to service via the router base.
    if (inferred === "service" && browserPath.includes("/dtools-json-upload/")) {
      applyModule("sales");
      return;
    }
    applyModule(inferred);
  }, [location, applyModule]);

  const mode: PortalMode = module === "sales" ? "sales" : "service";

  // Apply to <html> for theme variables + 300ms color transitions.
  useLayoutEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("data-portal-mode", mode);
  }, [mode]);

  const value = useMemo(
    () => ({
      module,
      mode,
      isSales: module === "sales",
      isProcurement: module === "procurement",
      setModule,
      setMode,
      enterSales,
      enterService,
      enterProcurement,
    }),
    [module, mode, setModule, setMode, enterSales, enterService, enterProcurement],
  );

  return <PortalModeContext.Provider value={value}>{children}</PortalModeContext.Provider>;
}

export function usePortalMode() {
  const ctx = useContext(PortalModeContext);
  if (!ctx) {
    throw new Error("usePortalMode must be used within PortalModeProvider");
  }
  return ctx;
}
